import { v4 as uuidv4 } from 'uuid';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "src/@/components/ui/select";
import { Button } from "src/@/components/ui/button";
import Iconify from 'src/components/Iconify';
import VariableInput from '../components/VariableInput';
import BlockRenderer from './BlockRenderer';
import { automationActions, conditionFieldsByTrigger } from 'src/config';

export default function CardCondition({ block, changeBlock, availableVariables = [], actionPhase, actionState, canais, user, hideSubBlocks = false }) {

    const availableFields = conditionFieldsByTrigger[actionPhase?.value] || [];
    const groupedFields = availableFields.reduce((groups, field) => {
        if (!groups[field.category]) groups[field.category] = [];
        groups[field.category].push(field);
        return groups;
    }, {});

    // Filter out to.condition from sub-block options to prevent infinite nesting
    const subBlockActions = automationActions.filter(a => a.value !== 'to.condition' && a.value !== 'to.wait' && a.accepts.includes(actionPhase?.value));

    const groupedSubActions = subBlockActions.reduce((groups, action) => {
        if (!groups[action.category]) groups[action.category] = [];
        groups[action.category].push(action);
        return groups;
    }, {});

    // --- Conditions logic (from CardConditions.js) ---
    const handleAddCondition = () => {
        const newConditions = [...(block.data?.conditions || []), { type: 'String', value1: '', operation: 'Contains', value2: '' }];
        changeBlock({ name: 'conditions', value: newConditions });
    };

    const handleRemoveCondition = (index) => {
        const newConditions = [...(block.data?.conditions || [])];
        newConditions.splice(index, 1);
        changeBlock({ name: 'conditions', value: newConditions });
    };

    const handleUpdateCondition = (index, field, value) => {
        const newConditions = [...(block.data?.conditions || [])];
        newConditions[index] = { ...newConditions[index], [field]: value };
        changeBlock({ name: 'conditions', value: newConditions });
    };

    // --- Sub-block management ---
    const handleAddSubBlock = (path) => {
        const current = block.data?.[path] || [];
        changeBlock({ name: path, value: [...current, { id: uuidv4(), value: null, data: {} }] });
    };

    const handleRemoveSubBlock = (path, id) => {
        const current = block.data?.[path] || [];
        changeBlock({ name: path, value: current.filter(b => b.id !== id) });
    };

    const handleUpdateSubBlockType = (path, id, value) => {
        const current = block.data?.[path] || [];
        changeBlock({ name: path, value: current.map(b => b.id === id ? { ...b, value, data: {} } : b) });
    };

    const handleChangeSubBlock = (path, id, event) => {
        const current = block.data?.[path] || [];
        if (event.name === "chatFiles") {
            const subBlock = current.find(b => b.id === id);
            let updatedChatFiles;
            if (event.action === "delete") {
                updatedChatFiles = event.value;
            } else {
                updatedChatFiles = [
                    ...(subBlock?.data?.chatFiles || []),
                    ...(Array.isArray(event.value) ? event.value : [event.value])
                ];
            }
            changeBlock({
                name: path,
                value: current.map(b => b.id === id ? { ...b, data: { ...b.data, chatFiles: updatedChatFiles } } : b)
            });
            return;
        }
        changeBlock({
            name: path,
            value: current.map(b => b.id === id ? { ...b, data: { ...b.data, [event.name]: event.value } } : b)
        });
    };

    const renderSubBlocks = (path, accentColor) => {
        const subBlocks = block.data?.[path] || [];
        const borderColor = accentColor === 'green' ? 'border-green-200' : 'border-red-200';
        const bgColor = accentColor === 'green' ? 'bg-green-50/50' : 'bg-red-50/50';
        const dotColor = accentColor === 'green' ? 'bg-green-500' : 'bg-red-500';
        const labelColor = accentColor === 'green' ? 'text-green-700' : 'text-red-700';
        const iconBg = accentColor === 'green' ? 'bg-green-100' : 'bg-red-100';
        const label = accentColor === 'green' ? 'Verdadeiro (Se)' : 'Falso (Senão)';
        const icon = accentColor === 'green' ? 'mdi:check-circle-outline' : 'mdi:close-circle-outline';

        return (
            <div className={`rounded-xl border ${borderColor} ${bgColor} p-4 space-y-3`}>
                <div className="flex items-center gap-2 mb-2">
                    <div className={`w-7 h-7 rounded-lg ${iconBg} flex items-center justify-center`}>
                        <Iconify icon={icon} width={16} className={labelColor} />
                    </div>
                    <span className={`text-sm font-semibold ${labelColor}`}>{label}</span>
                </div>

                {subBlocks.map((subBlock, index) => (
                    <div key={subBlock.id} className="bg-white rounded-lg border border-gray-200 p-3 space-y-3">
                        <div className="flex items-center gap-2">
                            <Select
                                value={subBlock.value ?? undefined}
                                onValueChange={(val) => handleUpdateSubBlockType(path, subBlock.id, val)}
                            >
                                <SelectTrigger className="flex-1 bg-white">
                                    <SelectValue placeholder="Selecione uma ação" />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.keys(groupedSubActions).map((category) => (
                                        <div key={category}>
                                            <h3 className="font-bold text-sm text-gray-700 capitalize px-2 py-1">{category}</h3>
                                            {groupedSubActions[category].map((event) => (
                                                <SelectItem key={event.value} value={event.value}>
                                                    <div className="flex items-center gap-2">
                                                        <Iconify icon={event.icon} className="mr-1" />
                                                        {event.name}
                                                    </div>
                                                </SelectItem>
                                            ))}
                                        </div>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-400 hover:text-red-600 hover:bg-red-50"
                                onClick={() => handleRemoveSubBlock(path, subBlock.id)}
                            >
                                <Iconify icon="mdi:delete-outline" width={18} />
                            </Button>
                        </div>

                        {subBlock.value && (
                            <BlockRenderer
                                block={subBlock}
                                changeBlock={(event) => handleChangeSubBlock(path, subBlock.id, event)}
                                availableVariables={availableVariables}
                                actionPhase={actionPhase}
                                actionState={actionState}
                                canais={canais}
                                user={user}
                            />
                        )}
                    </div>
                ))}

                <Button
                    variant="outline"
                    size="sm"
                    className={`w-full border-dashed ${borderColor}`}
                    onClick={() => handleAddSubBlock(path)}
                >
                    <Iconify icon="mdi:plus" className="mr-1" width={16} />
                    Adicionar ação
                </Button>
            </div>
        );
    };

    return (
        <div className="space-y-5">
            {/* Conditions Section */}
            <div className="space-y-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-gray-700">Condições</h3>
                        <span className="text-red-500">*</span>
                    </div>
                    <Select
                        value={block.data?.conditionLogic || 'and'}
                        onValueChange={(val) => changeBlock({ name: 'conditionLogic', value: val })}
                    >
                        <SelectTrigger className="w-28 bg-white h-8">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="and">E (AND)</SelectItem>
                            <SelectItem value="or">OU (OR)</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {(block.data?.conditions || []).map((condition, index) => (
                    <div key={index} className="p-4 border border-gray-200 rounded-lg space-y-4 bg-white shadow-sm relative group">
                        <button
                            onClick={() => handleRemoveCondition(index)}
                            className="absolute top-2 right-2 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                            <Iconify icon="mdi:close" width={18} />
                        </button>

                        <div className="grid grid-cols-3 gap-2 items-start">
                            <div>
                                <label className="text-xs font-semibold text-gray-500 mb-1 block">Campo</label>
                                {availableFields.length > 0 ? (
                                    <Select
                                        value={condition.value1 || undefined}
                                        onValueChange={(val) => {
                                            const field = availableFields.find(f => `{{${f.key}}}` === val);
                                            const newConditions = [...(block.data?.conditions || [])];
                                            newConditions[index] = { ...newConditions[index], value1: val, ...(field ? { type: field.type } : {}) };
                                            changeBlock({ name: 'conditions', value: newConditions });
                                        }}
                                    >
                                        <SelectTrigger className="w-full bg-white"><SelectValue placeholder="Selecione um campo" /></SelectTrigger>
                                        <SelectContent>
                                            {Object.keys(groupedFields).map((category) => (
                                                <SelectGroup key={category}>
                                                    <SelectLabel>{category}</SelectLabel>
                                                    {groupedFields[category].map((field) => (
                                                        <SelectItem key={field.key} value={`{{${field.key}}}`}>
                                                            {field.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectGroup>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                ) : (
                                    <VariableInput
                                        value={condition.value1 || ''}
                                        onChange={(e) => handleUpdateCondition(index, 'value1', e.target.value)}
                                        placeholder="Valor ou {{variavel}}"
                                        className="bg-white"
                                        variables={availableVariables}
                                    />
                                )}
                            </div>

                            <div>
                                <label className="text-xs font-semibold text-gray-500 mb-1 block">Operação</label>
                                <Select
                                    value={condition.operation || 'Contains'}
                                    onValueChange={(val) => handleUpdateCondition(index, 'operation', val)}
                                >
                                    <SelectTrigger className="w-full bg-white"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Equals">Igual</SelectItem>
                                        <SelectItem value="Not Equals">Não igual</SelectItem>
                                        <SelectItem value="Contains">Contém</SelectItem>
                                        <SelectItem value="Does not contain">Não contém</SelectItem>
                                        <SelectItem value="Starts with">Começa com</SelectItem>
                                        <SelectItem value="Ends with">Termina com</SelectItem>
                                        <SelectItem value="Is empty">Vazio</SelectItem>
                                        <SelectItem value="Is not empty">Não vazio</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <label className="text-xs font-semibold text-gray-500 mb-1 block">Valor 2</label>
                                <VariableInput
                                    value={condition.value2 || ''}
                                    onChange={(e) => handleUpdateCondition(index, 'value2', e.target.value)}
                                    placeholder="Valor ou {{variavel}}"
                                    className="bg-white"
                                    variables={availableVariables}
                                />
                            </div>
                        </div>
                    </div>
                ))}

                <Button
                    variant="outline"
                    className="w-full border-dashed border-2 py-5"
                    onClick={handleAddCondition}
                >
                    <Iconify icon="mdi:plus" className="mr-2" />
                    Adicionar Condição
                </Button>
            </div>

            {/* Branching Paths - hidden when visual branching is active on canvas */}
            {!hideSubBlocks && renderSubBlocks('truePath', 'green')}
            {!hideSubBlocks && renderSubBlocks('falsePath', 'red')}
        </div>
    );
}
